import React from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ChevronLeft,
  ChevronRight,
  FileText,
  FileSpreadsheet,
  Calendar,
  CalendarPlus,
  Link,
  Table,
  Loader2,
  Clock,
} from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface PlannerToolbarProps {
  viewType: 'month' | 'year';
  setViewType: (type: 'month' | 'year') => void;
  selectedDate: { month: number; year: number };
  setSelectedDate: React.Dispatch<React.SetStateAction<{ month: number; year: number }>>;
  locationName: string;
  coordinates: { lat: number; lon: number };
  timezone: string;
  isSubscribed: boolean;
  onDownloadPDF: () => void;
  onExportExcel: () => void;
  onExportCSV: () => void;
  onExportICS: () => void;
  onCopyLink: () => void;
  onShareCalendar: () => void;
  onOpenImportGuide: () => void;
  isDownloadingPDF: boolean;
  isExporting: boolean;
  isCreatingCalendar: boolean;
  icsEventCount: number;
}

const monthNames = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const monthAbbrevs = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const PlannerToolbar: React.FC<PlannerToolbarProps> = ({
  viewType,
  setViewType,
  selectedDate,
  setSelectedDate,
  locationName,
  coordinates,
  timezone,
  isSubscribed,
  onDownloadPDF,
  onExportExcel,
  onExportCSV,
  onExportICS,
  onCopyLink,
  onShareCalendar,
  onOpenImportGuide,
  isDownloadingPDF,
  isExporting,
  isCreatingCalendar,
  icsEventCount,
}) => {
  const isMobile = useIsMobile();

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 2 + i);

  const goToPrevMonth = () => {
    setSelectedDate(prev => {
      if (prev.month === 0) return { month: 11, year: prev.year - 1 };
      return { ...prev, month: prev.month - 1 };
    });
  };

  const goToNextMonth = () => {
    setSelectedDate(prev => {
      if (prev.month === 11) return { month: 0, year: prev.year + 1 };
      return { ...prev, month: prev.month + 1 };
    });
  };

  return (
    <div className="space-y-3">
      {/* Row 1: Navigation + View Toggle + Exports */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Left: Month/Year nav */}
        <div className="flex items-center gap-2">
          {viewType === 'month' && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={goToPrevMonth}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}
          <h2 className="text-lg font-semibold text-foreground whitespace-nowrap">
            {viewType === 'year'
              ? selectedDate.year
              : `${monthNames[selectedDate.month]} ${selectedDate.year}`}
          </h2>
          {viewType === 'month' && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={goToNextMonth}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
          <Select
            value={selectedDate.year.toString()}
            onValueChange={(value) =>
              setSelectedDate(prev => ({ ...prev, year: parseInt(value) }))
            }
          >
            <SelectTrigger className="w-20 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map((year) => (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex gap-1 ml-2">
            <Button
              variant={viewType === 'month' ? 'default' : 'outline'}
              size="sm"
              className="h-8 text-xs px-3"
              onClick={() => setViewType('month')}
            >
              Monthly
            </Button>
            <Button
              variant={viewType === 'year' ? 'default' : 'outline'}
              size="sm"
              className="h-8 text-xs px-3"
              onClick={() => setViewType('year')}
            >
              Yearly
            </Button>
          </div>
        </div>

        {/* Right: Export buttons */}
        {isSubscribed && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={onCopyLink}
              variant="outline"
              size="sm"
              className="h-8 text-xs"
            >
              <Link className="mr-1.5 h-3.5 w-3.5" />
              Copy Link
            </Button>
            <Button
              onClick={onShareCalendar}
              disabled={isCreatingCalendar}
              variant="outline"
              size="sm"
              className="h-8 text-xs"
            >
              {isCreatingCalendar ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <CalendarPlus className="mr-1.5 h-3.5 w-3.5" />
              )}
              Annual Calendar
            </Button>
            <Button
              onClick={onDownloadPDF}
              variant="outline"
              size="sm"
              className="h-8 text-xs"
              disabled={isDownloadingPDF}
            >
              {isDownloadingPDF ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileText className="mr-1.5 h-3.5 w-3.5" />
              )}
              PDF
            </Button>
            <Button onClick={onExportExcel} variant="outline" size="sm" className="h-8 text-xs">
              <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" />
              Excel
            </Button>
            {viewType === 'month' && (
              <Button onClick={onExportCSV} variant="outline" size="sm" className="h-8 text-xs">
                <Table className="mr-1.5 h-3.5 w-3.5" />
                CSV
              </Button>
            )}
            {viewType === 'month' && (
              <Button
                onClick={onExportICS}
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                disabled={isExporting}
              >
                {isExporting ? (
                  <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Calendar className="mr-1.5 h-3.5 w-3.5" />
                )}
                Calendar{icsEventCount > 0 ? ` (${icsEventCount})` : ''}
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Row 2: Location info + timezone */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <span>{locationName}</span>
        <span className="text-muted-foreground/40">·</span>
        <span>{coordinates.lat.toFixed(4)}°, {coordinates.lon.toFixed(4)}°</span>
        <span className="text-muted-foreground/40">·</span>
        <Clock className="w-3 h-3" />
        <span>{timezone}</span>
        {isSubscribed && viewType === 'month' && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <button
              onClick={onOpenImportGuide}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors underline"
            >
              Import guide
            </button>
          </>
        )}
      </div>

      {/* Row 3: Month tabs (month view only) */}
      {viewType === 'month' && (
        <Tabs
          value={selectedDate.month.toString()}
          onValueChange={(value) => setSelectedDate(prev => ({ ...prev, month: parseInt(value) }))}
          className="w-full"
        >
          <TabsList className="grid grid-cols-6 md:grid-cols-12 gap-1 h-auto p-1 bg-secondary/30 rounded-lg">
            {monthAbbrevs.map((month, index) => (
              <TabsTrigger
                key={index}
                value={index.toString()}
                className="text-xs md:text-sm py-1.5 data-[state=active]:bg-foreground data-[state=active]:text-background"
              >
                {month}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}
    </div>
  );
};
